/**
 * Contract Submission Service
 * Handles contract submission with encryption and final processing
 */

import { supabase } from '@xanectech/supabase-client';
import type { Json } from '@xanectech/supabase-client';
import { createLogger, getErrorMessage } from '@xanectech/lib';
import type { Database } from '@xanectech/types/database-types';
import { getCachedMasterKey } from '@xanectech/encryption';
import { EnhancedDataCategoryService } from './EnhancedDataCategoryService';
import { decryptValue, encryptValue } from '../utils/personal-data/encryption';
import { uploadDocument } from './DocumentUploadService';
import { SameProjectCompanyDeliveryProvider } from './CompanyDeliveryProvider';

const logger = createLogger('ContractSubmissionService');

type ContractSubmissionRow =
  Database['public']['Tables']['contract_submissions']['Row'];

interface RelatedCompany {
  name: string | null;
}

interface RelatedDivision {
  name: string | null;
}

interface RelatedDataContract {
  id: string;
  name: string;
  purpose: string;
  expires_at: string | null;
  companies: RelatedCompany | RelatedCompany[] | null;
  company_divisions: RelatedDivision | RelatedDivision[] | null;
}

export interface UserContractSubmissionRecord extends ContractSubmissionRow {
  data_contracts: RelatedDataContract | RelatedDataContract[] | null;
}

export interface SubmittedFieldData {
  fieldId: string;
  value: string;
  fieldLabel?: string;
}

export interface SubmissionData {
  submissionId: string;
  fields: SubmittedFieldData[];
  submissionMetadata: {
    totalFields: number;
    submittedAt: string;
  };
}

export interface PersonalVaultSyncOptions {
  fieldIdByFieldName: Record<string, string>;
  sourceLabel?: string;
}

export interface PersonalVaultSyncResult {
  updatedFields: number;
  updatedCategories: number;
  createdEntries: number;
  skippedFields: number;
}

export interface SharedContractSyncResult {
  checkedSubmissions: number;
  updatedSubmissions: number;
  updatedFields: number;
}

interface SubmissionCompletionUpdate {
  completed_at?: string;
  signature_data: Json;
  signed_at: string;
  status?: 'completed';
}

interface CategoryEntryLite {
  id: string;
  category_id: string;
  is_primary: boolean | null;
  created_at: string | null;
}

interface EncryptedContractPayload {
  encrypted_payload: string;
  encryption_format: 'xanec-masterkey-json-v1';
}

interface ParsedMasterKeyPayload {
  encryptedData: string;
  iv: string;
}

export interface SubmissionEnvelopeRecipient {
  recipient_type: 'division' | 'user';
  recipient_id: string;
  key_id: string;
  wrap_alg: 'RSA-OAEP-256';
  wrapped_dek_b64: string;
}

export interface SubmissionEnvelopeV1 {
  version: 1;
  payload_format: 'contract-submission-fields-v1';
  content_alg: 'A256GCM';
  ciphertext_b64: string;
  iv_b64: string;
  aad: {
    submission_id: string;
    contract_id: string;
    company_id: string;
    division_id: string;
    user_id: string;
    schema_version: 1;
    created_at: string;
  };
  payload_hash_sha256_b64: string;
  recipients: SubmissionEnvelopeRecipient[];
  metadata: {
    submitted_at: string;
    synced_from_personal_data_at?: string;
  };
}

interface SubmissionContext {
  submissionId: string;
  contractId: string;
  userId: string;
  companyId: string;
  divisionId: string;
}

export interface SharedContractFieldRecord {
  id: string;
  submission_id: string;
  contract_id: string;
  company_id: string;
  division_id: string | null;
  field_definition_id: string;
  shared_at: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  field_definition: {
    field_name: string;
    field_label: string;
  } | null;
}

interface IdOnlyRow {
  id: string;
}

interface RecipientKeyRow {
  id: string;
  public_key_pem: string;
}

interface EnvelopeRecipientTarget {
  recipient_type: 'division' | 'user';
  recipient_id: string;
  key_id: string;
  public_key_pem: string;
}

interface ContractFieldRequirementNameMapRow {
  contract_id: string | null;
  field_id: string | null;
  field_definitions:
    | { field_name: string | null }
    | Array<{ field_name: string | null }>
    | null;
}

interface SharedSubmissionSyncRow {
  id: string;
  contract_id: string;
  submitted_data: Json | null;
  submitted_data_envelope: Json | null;
  status: string;
}

interface ContractContextRow {
  id: string;
  company_id: string | null;
  division_id: string | null;
}

const MISSING_DIVISION_KEY_ERROR_PREFIX =
  'Missing active division encryption key for division';

const isMissingDivisionKeyError = (error: unknown): boolean =>
  getErrorMessage(error).includes(MISSING_DIVISION_KEY_ERROR_PREFIX);

const isRecordValue = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const toBase64 = (input: Uint8Array): string => {
  let binary = '';
  for (const byte of input) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary);
};

const fromBase64 = (input: string): Uint8Array => {
  const binary = atob(input);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const pemToBinary = (pem: string): ArrayBuffer => {
  const normalizedPem = pem
    .replace('-----BEGIN PUBLIC KEY-----', '')
    .replace('-----END PUBLIC KEY-----', '')
    .replace(/\s+/g, '');

  const decoded = fromBase64(normalizedPem);
  return decoded.buffer.slice(
    decoded.byteOffset,
    decoded.byteOffset + decoded.byteLength,
  ) as ArrayBuffer;
};

const encryptEnvelopePayloadForRecipients = async (
  payloadString: string,
  recipients: EnvelopeRecipientTarget[],
): Promise<{
  ciphertextB64: string;
  ivB64: string;
  wrappedRecipients: SubmissionEnvelopeRecipient[];
}> => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    throw new Error('WebCrypto subtle API is not available');
  }

  const dekRaw = globalThis.crypto.getRandomValues(new Uint8Array(32));
  const aesKey = await subtle.importKey(
    'raw',
    dekRaw,
    { name: 'AES-GCM' },
    false,
    ['encrypt'],
  );

  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const plaintextBytes = new TextEncoder().encode(payloadString);
  const ciphertextBuffer = await subtle.encrypt(
    {
      name: 'AES-GCM',
      iv,
    },
    aesKey,
    plaintextBytes,
  );

  const wrappedRecipients = await Promise.all(
    recipients.map(async (recipient) => {
      const importedPublicKey = await subtle.importKey(
        'spki',
        pemToBinary(recipient.public_key_pem),
        {
          name: 'RSA-OAEP',
          hash: 'SHA-256',
        },
        false,
        ['encrypt'],
      );

      const wrappedDekBuffer = await subtle.encrypt(
        { name: 'RSA-OAEP' },
        importedPublicKey,
        dekRaw,
      );

      return {
        recipient_type: recipient.recipient_type,
        recipient_id: recipient.recipient_id,
        key_id: recipient.key_id,
        wrap_alg: 'RSA-OAEP-256',
        wrapped_dek_b64: toBase64(new Uint8Array(wrappedDekBuffer)),
      } as SubmissionEnvelopeRecipient;
    }),
  );

  return {
    ciphertextB64: toBase64(new Uint8Array(ciphertextBuffer)),
    ivB64: toBase64(iv),
    wrappedRecipients,
  };
};

const hashPayloadBase64 = async (payload: string): Promise<string> => {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    return '';
  }

  const encoder = new TextEncoder();
  const digest = await subtle.digest('SHA-256', encoder.encode(payload));
  return toBase64(new Uint8Array(digest));
};

const parseMasterKeyPayload = (
  encryptedPayload: string,
): ParsedMasterKeyPayload => {
  const parsed = JSON.parse(encryptedPayload) as unknown;
  if (!isRecordValue(parsed)) {
    throw new Error('Invalid encrypted payload format');
  }

  const encryptedData = parsed.encryptedData;
  const iv = parsed.iv;

  if (typeof encryptedData !== 'string' || typeof iv !== 'string') {
    throw new Error('Encrypted payload is missing encryptedData or iv');
  }

  return {
    encryptedData,
    iv,
  };
};

const isEncryptedContractPayload = (
  value: unknown,
): value is EncryptedContractPayload => {
  if (!isRecordValue(value)) {
    return false;
  }

  return (
    typeof value.encrypted_payload === 'string' &&
    value.encryption_format === 'xanec-masterkey-json-v1'
  );
};

export async function encryptContractJsonPayload(
  payload: Record<string, unknown>,
): Promise<Json> {
  const encryptedPayload = await encryptValue(JSON.stringify(payload));

  return {
    encrypted_payload: encryptedPayload,
    encryption_format: 'xanec-masterkey-json-v1',
  } as Json;
}

export async function decryptContractJsonPayload(
  payload: Json | null,
): Promise<Record<string, unknown> | null> {
  if (!payload) {
    return null;
  }

  if (isEncryptedContractPayload(payload)) {
    const decrypted = await decryptValue(payload.encrypted_payload);
    const parsed = JSON.parse(decrypted) as unknown;

    if (!isRecordValue(parsed)) {
      throw new Error('Decrypted contract payload is not an object');
    }

    return parsed;
  }

  // Phase 5: also handle the envelope v1 format (ciphertext_b64 + iv_b64)
  if (
    isRecordValue(payload) &&
    typeof payload.ciphertext_b64 === 'string' &&
    typeof payload.iv_b64 === 'string'
  ) {
    return decryptEnvelopeCiphertext(payload.ciphertext_b64, payload.iv_b64);
  }

  if (isRecordValue(payload)) {
    return payload;
  }

  return null;
}

const getSubmissionContext = async (
  submissionId: string,
): Promise<SubmissionContext> => {
  const { data: submission, error } = await supabase
    .from('contract_submissions')
    .select(
      `
      id,
      contract_id,
      user_id,
      data_contracts!inner(
        company_id,
        division_id
      )
    `,
    )
    .eq('id', submissionId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!submission?.id || !submission.contract_id || !submission.user_id) {
    throw new Error('Unable to resolve submission context');
  }

  const contractRelationRaw = submission.data_contracts as
    | { company_id: string | null; division_id: string | null }
    | Array<{ company_id: string | null; division_id: string | null }>
    | null;

  const contractRelation = Array.isArray(contractRelationRaw)
    ? (contractRelationRaw[0] ?? null)
    : contractRelationRaw;

  if (!contractRelation?.company_id || !contractRelation?.division_id) {
    throw new Error('Contract is missing company/division context');
  }

  return {
    submissionId: submission.id,
    contractId: submission.contract_id,
    userId: submission.user_id,
    companyId: contractRelation.company_id,
    divisionId: contractRelation.division_id,
  };
};

const getContractFieldNameToIdMap = async (
  contractId: string,
): Promise<Map<string, string>> => {
  const { data: requirements, error } = await supabase
    .from('contract_field_requirements')
    .select(
      `
      field_id,
      field_definitions:field_id (
        field_name
      )
    `,
    )
    .eq('contract_id', contractId);

  if (error) {
    throw error;
  }

  const fieldNameToIdMap = new Map<string, string>();
  for (const requirementRaw of (requirements ??
    []) as ContractFieldRequirementNameMapRow[]) {
    const requirement = requirementRaw;

    const fieldId = requirement.field_id;
    const fieldDefinitionRaw = requirement.field_definitions;
    const fieldDefinition = Array.isArray(fieldDefinitionRaw)
      ? (fieldDefinitionRaw[0] ?? null)
      : fieldDefinitionRaw;
    const fieldName = fieldDefinition?.field_name;

    if (fieldId && fieldName) {
      fieldNameToIdMap.set(fieldName, fieldId);
    }
  }

  return fieldNameToIdMap;
};

export async function createEnvelopeRecipients(
  context: SubmissionContext,
): Promise<SubmissionEnvelopeRecipient[]> {
  const recipients: SubmissionEnvelopeRecipient[] = [];

  const { data: divisionKeyRaw } = await supabase
    .from('company_division_encryption_keys')
    .select('id')
    .eq('division_id', context.divisionId)
    .eq('is_active', true)
    .order('key_version', { ascending: false })
    .limit(1)
    .maybeSingle();

  const divisionKey = divisionKeyRaw as IdOnlyRow | null;

  if (!divisionKey?.id) {
    throw new Error(
      `Missing active division encryption key for division ${context.divisionId}`,
    );
  }

  recipients.push({
    recipient_type: 'division',
    recipient_id: context.divisionId,
    key_id: divisionKey.id,
    wrap_alg: 'RSA-OAEP-256',
    wrapped_dek_b64: 'phase1-placeholder',
  });

  const { data: userKeyRaw } = await supabase
    .from('public_keys')
    .select('id')
    .eq('user_id', context.userId)
    .eq('is_active', true)
    .order('key_version', { ascending: false })
    .limit(1)
    .maybeSingle();

  const userKey = userKeyRaw as IdOnlyRow | null;

  if (userKey?.id) {
    recipients.push({
      recipient_type: 'user',
      recipient_id: context.userId,
      key_id: userKey.id,
      wrap_alg: 'RSA-OAEP-256',
      wrapped_dek_b64: 'phase1-placeholder',
    });
  }

  return recipients;
}

const getEnvelopeRecipientTargets = async (
  context: SubmissionContext,
): Promise<EnvelopeRecipientTarget[]> => {
  const targets: EnvelopeRecipientTarget[] = [];

  const { data: divisionKeyRaw } = await supabase
    .from('company_division_encryption_keys')
    .select('id, public_key_pem')
    .eq('division_id', context.divisionId)
    .eq('is_active', true)
    .order('key_version', { ascending: false })
    .limit(1)
    .maybeSingle();

  const divisionKey = divisionKeyRaw as RecipientKeyRow | null;
  if (!divisionKey?.id || !divisionKey.public_key_pem) {
    throw new Error(
      `Missing active division encryption key for division ${context.divisionId}`,
    );
  }

  targets.push({
    recipient_type: 'division',
    recipient_id: context.divisionId,
    key_id: divisionKey.id,
    public_key_pem: divisionKey.public_key_pem,
  });

  const { data: userKeyRaw } = await supabase
    .from('public_keys')
    .select('id, public_key_pem')
    .eq('user_id', context.userId)
    .eq('is_active', true)
    .order('key_version', { ascending: false })
    .limit(1)
    .maybeSingle();

  const userKey = userKeyRaw as RecipientKeyRow | null;
  if (userKey?.id && userKey.public_key_pem) {
    targets.push({
      recipient_type: 'user',
      recipient_id: context.userId,
      key_id: userKey.id,
      public_key_pem: userKey.public_key_pem,
    });
  }

  return targets;
};

export async function buildSubmissionEnvelopeV1(
  submissionPayload: Record<string, unknown>,
  context: SubmissionContext,
): Promise<SubmissionEnvelopeV1> {
  const payloadString = JSON.stringify(submissionPayload);
  const payloadHash = await hashPayloadBase64(payloadString);
  const recipientTargets = await getEnvelopeRecipientTargets(context);
  const encryptedEnvelope = await encryptEnvelopePayloadForRecipients(
    payloadString,
    recipientTargets,
  );
  const createdAt = new Date().toISOString();

  return {
    version: 1,
    payload_format: 'contract-submission-fields-v1',
    content_alg: 'A256GCM',
    ciphertext_b64: encryptedEnvelope.ciphertextB64,
    iv_b64: encryptedEnvelope.ivB64,
    aad: {
      submission_id: context.submissionId,
      contract_id: context.contractId,
      company_id: context.companyId,
      division_id: context.divisionId,
      user_id: context.userId,
      schema_version: 1,
      created_at: createdAt,
    },
    payload_hash_sha256_b64: payloadHash,
    recipients: encryptedEnvelope.wrappedRecipients,
    metadata: {
      submitted_at: createdAt,
    },
  };
}

/**
 * Decrypts an envelope's ciphertext using the master key.
 * Reconstructs the master-key JSON payload `{encryptedData, iv}` from the
 * envelope's split `ciphertext_b64` and `iv_b64` fields, then calls
 * `decryptValue` and parses the result into a plain record.
 */
const decryptEnvelopeCiphertext = async (
  ciphertext_b64: string,
  iv_b64: string,
): Promise<Record<string, unknown>> => {
  const encryptedPayloadJson = JSON.stringify({
    encryptedData: ciphertext_b64,
    iv: iv_b64,
  });
  const decrypted = await decryptValue(encryptedPayloadJson);
  const parsed = JSON.parse(decrypted) as unknown;
  if (!isRecordValue(parsed)) {
    throw new Error('Decrypted envelope payload is not a valid record');
  }
  return parsed;
};

/**
 * Verifies the integrity of a decrypted envelope payload against its stored SHA-256 hash.
 * Returns `true` when the hash matches or when the stored hash is empty (pre-hash envelopes).
 * Returns `false` when a non-empty stored hash does not match the computed hash.
 * Callers should treat a `false` result as a tamper / corruption alert.
 */
const verifyEnvelopePayloadHash = async (
  decryptedPayloadString: string,
  storedHash: string,
): Promise<boolean> => {
  if (!storedHash) {
    return true;
  }

  const computed = await hashPayloadBase64(decryptedPayloadString);
  return computed === storedHash;
};

/**
 * Decrypts the ciphertext in `existingEnvelope`, applies `fieldUpdatesByFieldName`
 * (a mapping of field label/name → new value, matching the `fieldId` values stored
 * in the payload's `fields` array), re-encrypts the patched payload, and rebuilds
 * the envelope for persistence.
 *
 * - Preserves the original `aad` (including `created_at`) to maintain cryptographic lineage.
 * - Sets `metadata.synced_from_personal_data_at` to the current ISO timestamp.
 *
 * @param existingEnvelope - The current stored `SubmissionEnvelopeV1`.
 * @param fieldUpdatesByFieldName - Map of field name (as stored in payload `fieldId`) to new value.
 * @param context - Submission context used to fetch recipient keys for wrapping.
 * @returns The rebuilt envelope, the number of fields that changed, and the decrypted+patched payload.
 */
export async function patchAndReEncryptEnvelope(
  existingEnvelope: SubmissionEnvelopeV1,
  fieldUpdatesByFieldName: Record<string, string>,
  context: SubmissionContext,
): Promise<{
  envelope: SubmissionEnvelopeV1;
  patchedCount: number;
  patchedPayload: Record<string, unknown>;
}> {
  const decryptedPayload = await decryptEnvelopeCiphertext(
    existingEnvelope.ciphertext_b64,
    existingEnvelope.iv_b64,
  );

  // Phase 3 integrity check / Phase 4 tamper alert
  const originalPayloadString = JSON.stringify(decryptedPayload);
  const hashValid = await verifyEnvelopePayloadHash(
    originalPayloadString,
    existingEnvelope.payload_hash_sha256_b64,
  );
  if (!hashValid) {
    logger.warn('Envelope payload hash mismatch detected before patch', {
      submissionId: context.submissionId,
      envelopeCreatedAt: existingEnvelope.aad.created_at,
    });
  }

  // Phase 4: alert on missing recipients (key mismatch risk)
  if (existingEnvelope.recipients.length === 0) {
    logger.warn('Envelope has no recipients; wrapped DEK unavailable', {
      submissionId: context.submissionId,
      divisionId: context.divisionId,
    });
  }

  let patchedCount = 0;

  if (Array.isArray(decryptedPayload.fields)) {
    const patchedFields = (decryptedPayload.fields as unknown[]).map(
      (fieldItem) => {
        if (!fieldItem || typeof fieldItem !== 'object') {
          return fieldItem;
        }

        const currentItem = fieldItem as { fieldId?: unknown; value?: unknown };

        if (typeof currentItem.fieldId !== 'string') {
          return fieldItem;
        }

        const newValue = fieldUpdatesByFieldName[currentItem.fieldId];
        if (typeof newValue !== 'string') {
          return fieldItem;
        }

        if (currentItem.value !== newValue) {
          patchedCount += 1;
          return { ...currentItem, value: newValue };
        }

        return fieldItem;
      },
    );

    decryptedPayload.fields = patchedFields;
  }

  const syncedAt = new Date().toISOString();
  const payloadString = JSON.stringify(decryptedPayload);
  const payloadHash = await hashPayloadBase64(payloadString);
  const recipientTargets = await getEnvelopeRecipientTargets(context);
  const encryptedEnvelope = await encryptEnvelopePayloadForRecipients(
    payloadString,
    recipientTargets,
  );

  const envelope: SubmissionEnvelopeV1 = {
    version: 1,
    payload_format: 'contract-submission-fields-v1',
    content_alg: 'A256GCM',
    ciphertext_b64: encryptedEnvelope.ciphertextB64,
    iv_b64: encryptedEnvelope.ivB64,
    aad: { ...existingEnvelope.aad }, // preserve original aad.created_at to maintain cryptographic lineage
    payload_hash_sha256_b64: payloadHash,
    recipients: encryptedEnvelope.wrappedRecipients,
    metadata: {
      ...existingEnvelope.metadata,
      synced_from_personal_data_at: syncedAt,
    },
  };

  return { envelope, patchedCount, patchedPayload: decryptedPayload };
}

export async function upsertContractSubmissionSharedFields(
  submissionId: string,
  fieldDefinitionIds: string[],
  context?: SubmissionContext,
): Promise<void> {
  const submissionContext =
    context ?? (await getSubmissionContext(submissionId));
  const dedupedFieldIds = Array.from(new Set(fieldDefinitionIds));

  if (dedupedFieldIds.length > 0) {
    const upsertRows = dedupedFieldIds.map((fieldDefinitionId) => ({
      submission_id: submissionContext.submissionId,
      user_id: submissionContext.userId,
      contract_id: submissionContext.contractId,
      company_id: submissionContext.companyId,
      division_id: submissionContext.divisionId,
      field_definition_id: fieldDefinitionId,
      shared_at: new Date().toISOString(),
      is_active: true,
      updated_at: new Date().toISOString(),
    }));

    const { error: upsertError } = await supabase
      .from('contract_submission_shared_fields')
      .upsert(upsertRows, {
        onConflict: 'submission_id,field_definition_id',
      });

    if (upsertError) {
      throw upsertError;
    }

    const { error: deactivateError } = await supabase
      .from('contract_submission_shared_fields')
      .update({
        is_active: false,
        updated_at: new Date().toISOString(),
      })
      .eq('submission_id', submissionContext.submissionId)
      .not(
        'field_definition_id',
        'in',
        `(${dedupedFieldIds.map((id) => `"${id}"`).join(',')})`,
      );

    if (deactivateError) {
      throw deactivateError;
    }

    return;
  }

  const { error: deactivateAllError } = await supabase
    .from('contract_submission_shared_fields')
    .update({
      is_active: false,
      updated_at: new Date().toISOString(),
    })
    .eq('submission_id', submissionContext.submissionId);

  if (deactivateAllError) {
    throw deactivateAllError;
  }
}

export async function syncSharedFieldsFromSubmissionEnvelope(
  submissionId: string,
  submissionPayload: Record<string, unknown>,
  context?: SubmissionContext,
): Promise<void> {
  const submissionContext =
    context ?? (await getSubmissionContext(submissionId));
  const fieldNameToIdMap = await getContractFieldNameToIdMap(
    submissionContext.contractId,
  );

  const fieldDefinitionIds = Array.isArray(submissionPayload.fields)
    ? (submissionPayload.fields as unknown[])
        .map((fieldItem) => {
          if (!fieldItem || typeof fieldItem !== 'object') {
            return null;
          }

          const currentField = fieldItem as {
            fieldId?: unknown;
            value?: unknown;
          };

          if (
            typeof currentField.fieldId !== 'string' ||
            typeof currentField.value !== 'string' ||
            currentField.value.trim().length === 0
          ) {
            return null;
          }

          return fieldNameToIdMap.get(currentField.fieldId) ?? null;
        })
        .filter((fieldId): fieldId is string => typeof fieldId === 'string')
    : [];

  await upsertContractSubmissionSharedFields(
    submissionId,
    fieldDefinitionIds,
    submissionContext,
  );
}

export async function listSharedFieldsForUser(
  userId: string,
): Promise<SharedContractFieldRecord[]> {
  const { data, error } = await supabase
    .from('contract_submission_shared_fields')
    .select(
      `
      id,
      submission_id,
      contract_id,
      company_id,
      division_id,
      field_definition_id,
      shared_at,
      is_active,
      created_at,
      updated_at,
      field_definition:field_definition_id (
        field_name,
        field_label
      )
    `,
    )
    .eq('user_id', userId)
    .eq('is_active', true)
    .order('shared_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []) as SharedContractFieldRecord[];
}

/**
 * Prepare contract fields for submission
 * Stores plaintext data (secured by Supabase at-rest encryption)
 */
export async function prepareContractFields(
  fieldData: Record<string, string>,
  fieldLabels?: Record<string, string>,
): Promise<SubmittedFieldData[]> {
  const fields: SubmittedFieldData[] = [];

  for (const [fieldId, value] of Object.entries(fieldData)) {
    fields.push({
      fieldId,
      value,
      fieldLabel: fieldLabels?.[fieldId],
    });
  }

  return fields;
}

/**
 * Submit contract data
 * Updates the submission record with plaintext data (Supabase at-rest encryption)
 */
export async function submitContractData(
  submissionId: string,
  fields: SubmittedFieldData[],
  options?: { retryCount?: number },
): Promise<void> {
  try {
    const submissionContext = await getSubmissionContext(submissionId);

    // Prepare submission data
    const submissionData = {
      fields: fields.map((f) => ({
        fieldId: f.fieldId,
        value: f.value,
        fieldLabel: f.fieldLabel,
      })),
      metadata: {
        totalFields: fields.length,
        submittedAt: new Date().toISOString(),
        retryCount: options?.retryCount || 0,
      },
    };

    const encryptedSubmittedData = await encryptContractJsonPayload(
      submissionData as Record<string, unknown>,
    );

    const submissionEnvelope = await buildSubmissionEnvelopeV1(
      submissionData as Record<string, unknown>,
      submissionContext,
    );

    // Update submission with encrypted data
    const { data: updatedSubmission, error: updateError } = await supabase
      .from('contract_submissions')
      .update({
        submitted_data: encryptedSubmittedData,
        submitted_data_envelope: submissionEnvelope as unknown as Json,
        envelope_version: 1,
        encrypted_at: new Date().toISOString(),
        submitted_at: new Date().toISOString(),
        status: 'submitted',
      })
      .eq('id', submissionId)
      .select('id, status')
      .maybeSingle();

    if (updateError) {
      logger.error('Failed to update contract submission with encrypted data', {
        error: getErrorMessage(updateError),
        submissionId,
      });
      throw new Error(
        `Failed to update contract submission with encrypted data. Please retry.`,
      );
    }
    if (!updatedSubmission) {
      logger.error('Contract submission record was not updated', {
        error: getErrorMessage(updateError),
        submissionId,
      });
      throw new Error(
        `Contract submission record was not updated. Please retry.`,
      );
    }

    await syncSharedFieldsFromSubmissionEnvelope(
      submissionId,
      submissionData as Record<string, unknown>,
      submissionContext,
    );

    logger.info('Contract submission envelope created and stored', {
      submissionId,
      fieldCount: fields.length,
      envelopeVersion: 1,
      recipientCount: submissionEnvelope.recipients.length,
      encryptedAt: new Date().toISOString(),
    });

    logger.info('Contract data submitted successfully', {
      submissionId,
      fieldCount: fields.length,
      retryCount: options?.retryCount || 0,
    });
  } catch (error) {
    logger.error(
      'Failed to submit contract data',
      {
        error: error as Error,
        stack: error instanceof Error ? error.stack : undefined,
        submissionId,
        retryCount: options?.retryCount || 0,
      },
      'submission',
    );

    if (isMissingDivisionKeyError(error)) {
      throw new Error(
        'Contract submission is temporarily unavailable because this company division does not have an active encryption key configured. Please contact the company administrator and try again.',
      );
    }

    throw new Error(`Failed to submit contract data. Please try again.`);
  }
}

export async function saveContractSubmissionDraft(
  submissionId: string,
  draftData: Record<string, string>,
): Promise<void> {
  const encryptedDraftData = await encryptContractJsonPayload(draftData);

  const { data: updatedSubmission, error } = await supabase
    .from('contract_submissions')
    .update({
      draft_data: encryptedDraftData,
      draft_saved_at: new Date().toISOString(),
    })
    .eq('id', submissionId)
    .select('id')
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!updatedSubmission) {
    throw new Error('Draft was not updated');
  }
}

export async function getContractSubmissionDraft(
  submissionId: string,
): Promise<Record<string, string> | null> {
  const { data: submission, error } = await supabase
    .from('contract_submissions')
    .select('draft_data')
    .eq('id', submissionId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!submission?.draft_data) {
    return null;
  }

  const decryptedDraft = await decryptContractJsonPayload(
    submission.draft_data as Json,
  );

  if (!decryptedDraft) {
    return null;
  }

  const normalizedDraft = Object.entries(decryptedDraft).reduce(
    (acc, [key, value]) => {
      if (typeof value === 'string') {
        acc[key] = value;
      }

      return acc;
    },
    {} as Record<string, string>,
  );

  return normalizedDraft;
}

/**
 * Sync submitted contract values to personal vault entries.
 * Only non-empty values that can be mapped to a known field ID are synced.
 */
export async function syncSubmittedDataToPersonalVault(
  submittedData: Record<string, string>,
  options: PersonalVaultSyncOptions,
): Promise<PersonalVaultSyncResult> {
  const fieldEntries = Object.entries(submittedData).filter(
    ([fieldName, value]) =>
      Boolean(options.fieldIdByFieldName[fieldName]) &&
      typeof value === 'string' &&
      value.trim().length > 0,
  );

  if (fieldEntries.length === 0) {
    return {
      updatedFields: 0,
      updatedCategories: 0,
      createdEntries: 0,
      skippedFields: Object.keys(submittedData).length,
    };
  }

  const mappedFieldIds = Array.from(
    new Set(
      fieldEntries
        .map(([fieldName]) => options.fieldIdByFieldName[fieldName])
        .filter(
          (fieldId): fieldId is string =>
            typeof fieldId === 'string' && fieldId.length > 0,
        ),
    ),
  );

  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) {
    throw userError;
  }

  if (!user?.id) {
    throw new Error('User not authenticated');
  }

  const { data: fieldDefinitions, error: fieldDefinitionsError } =
    await supabase
      .from('field_definitions')
      .select('id, category_id')
      .in('id', mappedFieldIds);

  if (fieldDefinitionsError) {
    throw fieldDefinitionsError;
  }

  const categoryByFieldId = new Map<string, string>();
  for (const fieldDefinition of fieldDefinitions ?? []) {
    if (fieldDefinition.id && fieldDefinition.category_id) {
      categoryByFieldId.set(fieldDefinition.id, fieldDefinition.category_id);
    }
  }

  const fieldValuesByCategory: Record<string, Record<string, string>> = {};
  let skippedFields = 0;

  for (const [fieldName, value] of fieldEntries) {
    const fieldId = options.fieldIdByFieldName[fieldName];
    if (!fieldId) {
      skippedFields += 1;
      continue;
    }

    const categoryId = categoryByFieldId.get(fieldId);

    if (!categoryId) {
      skippedFields += 1;
      continue;
    }

    if (!fieldValuesByCategory[categoryId]) {
      fieldValuesByCategory[categoryId] = {};
    }

    fieldValuesByCategory[categoryId][fieldId] = value;
  }

  const categoryIds = Object.keys(fieldValuesByCategory);
  if (categoryIds.length === 0) {
    return {
      updatedFields: 0,
      updatedCategories: 0,
      createdEntries: 0,
      skippedFields,
    };
  }

  const { data: existingEntries, error: existingEntriesError } = await supabase
    .from('category_entries')
    .select('id, category_id, is_primary, created_at')
    .eq('user_id', user.id)
    .in('category_id', categoryIds);

  if (existingEntriesError) {
    throw existingEntriesError;
  }

  const existingEntriesByCategory = new Map<string, CategoryEntryLite[]>();
  for (const entry of (existingEntries ?? []) as CategoryEntryLite[]) {
    const currentEntries =
      existingEntriesByCategory.get(entry.category_id) ?? [];
    currentEntries.push(entry);
    existingEntriesByCategory.set(entry.category_id, currentEntries);
  }

  let updatedFields = 0;
  let updatedCategories = 0;
  let createdEntries = 0;

  for (const categoryId of categoryIds) {
    const categoryFieldValues = fieldValuesByCategory[categoryId];
    if (!categoryFieldValues) {
      continue;
    }

    const categoryEntries = existingEntriesByCategory.get(categoryId) ?? [];

    const selectedEntry = categoryEntries.sort((left, right) => {
      if (left.is_primary === right.is_primary) {
        const leftDate = left.created_at ?? '';
        const rightDate = right.created_at ?? '';
        return leftDate.localeCompare(rightDate);
      }

      return left.is_primary ? -1 : 1;
    })[0];

    let targetEntryId = selectedEntry?.id;

    if (!targetEntryId) {
      const newEntry = await EnhancedDataCategoryService.createCategoryEntry({
        user_id: user.id,
        category_id: categoryId,
        entry_label: options.sourceLabel ?? 'Contract Submission Update',
        entry_description: 'Auto-updated from submitted contract data',
        is_primary: true,
        is_favorite: false,
        tags: null,
        label_manually_edited: false,
      });

      targetEntryId = newEntry.id;
      createdEntries += 1;
    }

    await EnhancedDataCategoryService.setEntryFieldDataBulk(
      targetEntryId,
      categoryFieldValues,
    );

    updatedCategories += 1;
    updatedFields += Object.keys(categoryFieldValues).length;
  }

  logger.info('Synced submitted contract values to personal vault', {
    updatedFields,
    updatedCategories,
    createdEntries,
    skippedFields,
  });

  return {
    updatedFields,
    updatedCategories,
    createdEntries,
    skippedFields,
  };
}

export async function syncSharedContractSubmissionsForFieldChanges(
  changedFieldValuesByFieldId: Record<string, string>,
): Promise<SharedContractSyncResult> {
  const changedFieldIds = Object.keys(changedFieldValuesByFieldId).filter(
    (fieldId) =>
      typeof changedFieldValuesByFieldId[fieldId] === 'string' &&
      changedFieldValuesByFieldId[fieldId].trim().length > 0,
  );

  if (changedFieldIds.length === 0) {
    return {
      checkedSubmissions: 0,
      updatedSubmissions: 0,
      updatedFields: 0,
    };
  }

  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser();

  if (userError) {
    throw userError;
  }

  if (!user?.id) {
    throw new Error('User not authenticated');
  }

  const { data: requirements, error: requirementsError } = await supabase
    .from('contract_field_requirements')
    .select(
      `
      contract_id,
      field_id,
      field_definitions:field_id (
        field_name
      )
    `,
    )
    .in('field_id', changedFieldIds);

  if (requirementsError) {
    throw requirementsError;
  }

  const contractFieldNameByFieldId = new Map<string, Map<string, string>>();
  for (const requirementRaw of (requirements ??
    []) as ContractFieldRequirementNameMapRow[]) {
    const requirement = requirementRaw;

    const contractId = requirement.contract_id;
    const fieldId = requirement.field_id;
    const fieldDefinition = Array.isArray(requirement.field_definitions)
      ? (requirement.field_definitions[0] ?? null)
      : requirement.field_definitions;
    const fieldName = fieldDefinition?.field_name;

    if (!contractId || !fieldId || !fieldName) {
      continue;
    }

    const mapping =
      contractFieldNameByFieldId.get(contractId) ?? new Map<string, string>();
    mapping.set(fieldId, fieldName);
    contractFieldNameByFieldId.set(contractId, mapping);
  }

  const contractIds = Array.from(contractFieldNameByFieldId.keys());
  if (contractIds.length === 0) {
    return {
      checkedSubmissions: 0,
      updatedSubmissions: 0,
      updatedFields: 0,
    };
  }

  const { data: submissions, error: submissionsError } = await supabase
    .from('contract_submissions')
    .select('id, contract_id, submitted_data, submitted_data_envelope, status')
    .eq('user_id', user.id)
    .in('contract_id', contractIds)
    .in('status', ['submitted', 'signed', 'completed']);

  if (submissionsError) {
    throw submissionsError;
  }

  const { data: contractsRaw, error: contractsError } = await supabase
    .from('data_contracts')
    .select('id, company_id, division_id')
    .in('id', contractIds);

  if (contractsError) {
    throw contractsError;
  }

  const contracts = (contractsRaw ?? []) as ContractContextRow[];

  const contractContextMap = new Map<
    string,
    { companyId: string | null; divisionId: string | null }
  >();
  for (const contract of contracts) {
    contractContextMap.set(contract.id, {
      companyId: contract.company_id,
      divisionId: contract.division_id,
    });
  }

  let updatedSubmissions = 0;
  let updatedFields = 0;

  for (const submission of (submissions ?? []) as SharedSubmissionSyncRow[]) {
    const hasEnvelope = isRecordValue(submission.submitted_data_envelope);
    const hasSubmittedData = submission.submitted_data !== null;

    if ((!hasEnvelope && !hasSubmittedData) || !submission.contract_id) {
      continue;
    }

    const contractFieldMap = contractFieldNameByFieldId.get(
      submission.contract_id,
    );

    if (!contractFieldMap || contractFieldMap.size === 0) {
      continue;
    }

    const fieldNameToIdMap = new Map(
      Array.from(contractFieldMap.entries()).map(([fieldId, fieldName]) => [
        fieldName,
        fieldId,
      ]),
    );

    const fieldUpdatesByFieldName: Record<string, string> = {};
    for (const [fieldId, fieldName] of contractFieldMap.entries()) {
      const changedValue = changedFieldValuesByFieldId[fieldId];
      if (typeof changedValue === 'string') {
        fieldUpdatesByFieldName[fieldName] = changedValue;
      }
    }

    if (Object.keys(fieldUpdatesByFieldName).length === 0) {
      continue;
    }

    const contextMeta = contractContextMap.get(submission.contract_id);
    if (!contextMeta?.companyId || !contextMeta.divisionId) {
      continue;
    }

    const submissionContext: SubmissionContext = {
      submissionId: submission.id,
      contractId: submission.contract_id,
      userId: user.id,
      companyId: contextMeta.companyId,
      divisionId: contextMeta.divisionId,
    };

    if (hasEnvelope) {
      // Phase 3: envelope-first sync path
      let patchResult: {
        envelope: SubmissionEnvelopeV1;
        patchedCount: number;
        patchedPayload: Record<string, unknown>;
      } | null = null;
      try {
        patchResult = await patchAndReEncryptEnvelope(
          submission.submitted_data_envelope as unknown as SubmissionEnvelopeV1,
          fieldUpdatesByFieldName,
          submissionContext,
        );
      } catch (patchError) {
        logger.warn(
          'Skipping contract submission sync: cannot patch envelope payload',
          {
            submissionId: submission.id,
            error: getErrorMessage(patchError),
          },
        );
        if (!hasSubmittedData) {
          continue;
        }
      }

      if (patchResult) {
        if (patchResult.patchedCount === 0) {
          continue;
        }

        const encryptedPayload = await encryptContractJsonPayload(
          patchResult.patchedPayload,
        );

        const { data: updatedSubmission, error: updateError } = await supabase
          .from('contract_submissions')
          .update({
            submitted_data: encryptedPayload,
            submitted_data_envelope: patchResult.envelope as unknown as Json,
            envelope_version: 1,
            encrypted_at: new Date().toISOString(),
          })
          .eq('id', submission.id)
          .select('id')
          .maybeSingle();

        if (updateError) {
          throw updateError;
        }

        if (!updatedSubmission) {
          throw new Error(
            `Shared contract submission update failed for submission ${submission.id}`,
          );
        }

        const activeFieldDefinitionIds = Array.isArray(
          patchResult.patchedPayload.fields,
        )
          ? (patchResult.patchedPayload.fields as unknown[])
              .map((fieldItem) => {
                if (!fieldItem || typeof fieldItem !== 'object') {
                  return null;
                }
                const currentItem = fieldItem as {
                  fieldId?: unknown;
                  value?: unknown;
                };
                if (
                  typeof currentItem.fieldId !== 'string' ||
                  typeof currentItem.value !== 'string' ||
                  currentItem.value.trim().length === 0
                ) {
                  return null;
                }
                return fieldNameToIdMap.get(currentItem.fieldId) ?? null;
              })
              .filter(
                (fieldId): fieldId is string => typeof fieldId === 'string',
              )
          : [];

        await upsertContractSubmissionSharedFields(
          submission.id,
          activeFieldDefinitionIds,
          submissionContext,
        );

        logger.info(
          'Contract submission envelope patched via personal data sync',
          {
            submissionId: submission.id,
            patchedCount: patchResult.patchedCount,
            syncedAt:
              patchResult.envelope.metadata.synced_from_personal_data_at,
          },
        );

        updatedSubmissions += 1;
        updatedFields += patchResult.patchedCount;
        continue;
      }
    }

    {
      // Legacy path: decrypt from submitted_data when no envelope present
      let decryptedPayload: Record<string, unknown> | null = null;
      try {
        decryptedPayload = await decryptContractJsonPayload(
          submission.submitted_data as Json,
        );
      } catch (decryptionError) {
        logger.warn(
          'Skipping contract submission sync: cannot decrypt payload',
          {
            submissionId: submission.id,
            error: getErrorMessage(decryptionError),
          },
        );
        continue;
      }

      if (!decryptedPayload) {
        continue;
      }

      let changedCount = 0;

      if (Array.isArray(decryptedPayload.fields)) {
        const updatedFieldsArray = (decryptedPayload.fields as unknown[]).map(
          (fieldItem) => {
            if (!fieldItem || typeof fieldItem !== 'object') {
              return fieldItem;
            }

            const currentItem = fieldItem as {
              fieldId?: unknown;
              value?: unknown;
            };

            if (typeof currentItem.fieldId !== 'string') {
              return fieldItem;
            }

            const newValue = fieldUpdatesByFieldName[currentItem.fieldId];
            if (typeof newValue !== 'string') {
              return fieldItem;
            }

            if (currentItem.value !== newValue) {
              changedCount += 1;
              return { ...currentItem, value: newValue };
            }

            return fieldItem;
          },
        );

        decryptedPayload = {
          ...decryptedPayload,
          fields: updatedFieldsArray,
          metadata: {
            ...(isRecordValue(decryptedPayload.metadata)
              ? decryptedPayload.metadata
              : {}),
            syncedAt: new Date().toISOString(),
            syncSource: 'personal-data-update',
          },
        };

        if (changedCount === 0) {
          continue;
        }

        const activeFieldDefinitionIds = updatedFieldsArray
          .map((fieldItem) => {
            if (!fieldItem || typeof fieldItem !== 'object') {
              return null;
            }
            const currentItem = fieldItem as {
              fieldId?: unknown;
              value?: unknown;
            };
            if (
              typeof currentItem.fieldId !== 'string' ||
              typeof currentItem.value !== 'string' ||
              currentItem.value.trim().length === 0
            ) {
              return null;
            }
            return fieldNameToIdMap.get(currentItem.fieldId) ?? null;
          })
          .filter((fieldId): fieldId is string => typeof fieldId === 'string');

        const encryptedPayload =
          await encryptContractJsonPayload(decryptedPayload);
        const envelopePayload = await buildSubmissionEnvelopeV1(
          decryptedPayload,
          submissionContext,
        );

        const { data: updatedSubmission, error: updateError } = await supabase
          .from('contract_submissions')
          .update({
            submitted_data: encryptedPayload,
            submitted_data_envelope: envelopePayload as unknown as Json,
            envelope_version: 1,
            encrypted_at: new Date().toISOString(),
          })
          .eq('id', submission.id)
          .select('id')
          .maybeSingle();

        if (updateError) {
          throw updateError;
        }

        if (!updatedSubmission) {
          throw new Error(
            `Shared contract submission update failed for submission ${submission.id}`,
          );
        }

        await upsertContractSubmissionSharedFields(
          submission.id,
          activeFieldDefinitionIds,
          submissionContext,
        );

        updatedSubmissions += 1;
        updatedFields += changedCount;
      }
    }
  }

  logger.info('Synced shared contract submissions from personal data changes', {
    checkedSubmissions: (submissions ?? []).length,
    updatedSubmissions,
    updatedFields,
  });

  return {
    checkedSubmissions: (submissions ?? []).length,
    updatedSubmissions,
    updatedFields,
  };
}

/**
 * Get all contract submissions for the currently authenticated user
 */
export async function getCurrentUserContractSubmissions(): Promise<
  UserContractSubmissionRecord[]
> {
  try {
    const {
      data: { user },
      error: userError,
    } = await supabase.auth.getUser();

    if (userError) {
      throw userError;
    }

    if (!user?.id) {
      return [];
    }

    const { data, error } = await supabase
      .from('contract_submissions')
      .select(
        `
        *,
        data_contracts (
          id,
          name,
          purpose,
          expires_at,
          companies (name),
          company_divisions (name)
        )
      `,
      )
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    const submissions = (data ?? []) as UserContractSubmissionRecord[];

    const decryptedSubmissions = await Promise.all(
      submissions.map(async (submission) => {
        // Phase 5: prefer envelope decryption when available
        const envelopeRaw = (submission as unknown as Record<string, unknown>)
          .submitted_data_envelope;
        if (isRecordValue(envelopeRaw)) {
          const env = envelopeRaw as {
            ciphertext_b64?: unknown;
            iv_b64?: unknown;
          };
          if (
            typeof env.ciphertext_b64 === 'string' &&
            typeof env.iv_b64 === 'string'
          ) {
            try {
              const decryptedFromEnvelope = await decryptEnvelopeCiphertext(
                env.ciphertext_b64,
                env.iv_b64,
              );
              return {
                ...submission,
                submitted_data: decryptedFromEnvelope as unknown as Json,
              };
            } catch (envelopeDecryptError) {
              logger.warn(
                'Failed to decrypt submission envelope; falling back to submitted_data',
                {
                  submissionId: submission.id,
                  error: getErrorMessage(envelopeDecryptError),
                },
              );
            }
          }
        }

        if (!submission.submitted_data) {
          return submission;
        }

        try {
          const decryptedSubmittedData = await decryptContractJsonPayload(
            submission.submitted_data as Json,
          );

          if (!decryptedSubmittedData) {
            return submission;
          }

          return {
            ...submission,
            submitted_data: decryptedSubmittedData as Json,
          };
        } catch (decryptionError) {
          logger.warn('Failed to decrypt contract submission payload', {
            submissionId: submission.id,
            error: getErrorMessage(decryptionError),
          });

          return submission;
        }
      }),
    );

    return decryptedSubmissions;
  } catch (error) {
    logger.error(
      'Failed to fetch current user contract submissions',
      {
        error: getErrorMessage(
          error,
          'Failed to fetch current user contract submissions',
        ),
      },
      'submission',
    );

    throw new Error('Failed to load contract submissions. Please try again.');
  }
}

const getFirstRelation = <T>(value: T | T[] | null | undefined): T | null => {
  if (!value) {
    return null;
  }

  return Array.isArray(value) ? (value[0] ?? null) : value;
};

const escapeCsvValue = (value: string) => {
  const escapedValue = value.replace(/"/g, '""');
  return `"${escapedValue}"`;
};

/**
 * Export all current user contract submissions to CSV
 */
export async function exportCurrentUserContractSubmissionsCsv(): Promise<number> {
  const submissions = await getCurrentUserContractSubmissions();

  const headers = [
    'Submission ID',
    'Contract Name',
    'Company',
    'Purpose',
    'Status',
    'Shared Date',
    'Expires Date',
  ];

  const rows = submissions.map((submission) => {
    const dataContract = getFirstRelation(submission.data_contracts);
    const company = getFirstRelation(dataContract?.companies);
    const sharedDate =
      submission.submitted_at ||
      submission.terms_accepted_at ||
      submission.created_at ||
      '';

    return [
      submission.id,
      dataContract?.name || 'N/A',
      company?.name || 'N/A',
      dataContract?.purpose || 'N/A',
      submission.status,
      sharedDate ? new Date(sharedDate).toLocaleDateString() : 'N/A',
      dataContract?.expires_at
        ? new Date(dataContract.expires_at).toLocaleDateString()
        : 'N/A',
    ].map((value) => escapeCsvValue(String(value)));
  });

  const csvContent = [
    headers.map((value) => escapeCsvValue(value)).join(','),
    ...rows.map((row) => row.join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = `contract-submissions-history-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return submissions.length;
}

/**
 * Complete contract submission after signature
 * Updates submission status, stores signature data, and processes final submission
 */
export async function completeContractSubmission(
  submissionId: string,
  signatureData: Json,
  signatureType: 'digital' | 'physical',
): Promise<void> {
  try {
    logger.info('Completing contract submission', {
      submissionId,
      signatureType,
    });

    const updateData: SubmissionCompletionUpdate = {
      signature_data: signatureData,
      signed_at: new Date().toISOString(),
    };

    // If digital signature, mark as completed immediately
    // If physical signature, status remains 'submitted' until PDF is uploaded
    if (signatureType === 'digital') {
      updateData.status = 'completed';
      updateData.completed_at = new Date().toISOString();
    }

    const { data: updatedSubmission, error: updateError } = await supabase
      .from('contract_submissions')
      .update(updateData)
      .eq('id', submissionId)
      .select('id, status')
      .maybeSingle();

    if (updateError) throw updateError;
    if (!updatedSubmission) {
      throw new Error(
        'Contract submission record was not updated during signature completion.',
      );
    }

    // Get submission details for notifications
    const { data: submission } = await supabase
      .from('contract_submissions')
      .select(
        `
        id,
        contract_id,
        user_id,
        invitation_token,
        data_contracts!inner(
          company_id,
          name
        )
      `,
      )
      .eq('id', submissionId)
      .single();

    // Update email invitation status if present
    if (submission?.invitation_token) {
      await updateEmailInvitationStatus(submission.invitation_token as string);
    }

    // Trigger confirmation notifications (placeholder for future edge function)
    await triggerConfirmationNotifications(submissionId);

    logger.info('Contract submission completed successfully', {
      submissionId,
      status: signatureType === 'digital' ? 'completed' : 'submitted',
    });
  } catch (error) {
    logger.error('Failed to complete submission', error as Error, 'submission');
    throw new Error(
      'Failed to complete contract submission. Please try again.',
    );
  }
}

/**
 * Upload signed PDF for physical signatures
 * Transitions submission from 'submitted' to 'completed'
 */
export async function uploadSignedPDF(
  submissionId: string,
  signedPdfFile: File,
): Promise<string> {
  try {
    // Upload PDF to storage
    const fileName = `${submissionId}/${Date.now()}_signed.pdf`;
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('contract-submissions')
      .upload(fileName, signedPdfFile, {
        contentType: 'application/pdf',
        upsert: false,
      });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('contract-submissions')
      .getPublicUrl(fileName);

    // Update submission with PDF path and complete it
    const { data: updatedSubmission, error: updateError } = await supabase
      .from('contract_submissions')
      .update({
        signed_pdf_path: urlData.publicUrl,
        status: 'completed',
        completed_at: new Date().toISOString(),
      })
      .eq('id', submissionId)
      .select('id, status')
      .maybeSingle();

    if (updateError) throw updateError;
    if (!updatedSubmission) {
      throw new Error(
        'Signed PDF uploaded, but submission status update failed.',
      );
    }

    return urlData.publicUrl;
  } catch (error) {
    logger.error(
      'Failed to upload signed PDF:',
      {
        error: getErrorMessage(error, 'Failed to upload signed PDF'),
        submissionId,
      },
      'submission',
    );
    throw new Error('Failed to upload signed document. Please try again.');
  }
}

/**
 * Get submission status
 */
export async function getSubmissionStatus(submissionId: string) {
  try {
    const { data, error } = await supabase
      .from('contract_submissions')
      .select('status, submitted_at, signed_at, completed_at')
      .eq('id', submissionId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error(
      'Failed to get submission status',
      {
        error: getErrorMessage(error, 'Failed to get submission status'),
        submissionId,
      },
      'submission',
    );
    throw new Error('Failed to retrieve submission status.');
  }
}

/**
 * Update email invitation status to submitted
 * Phase 6: Email invitation status tracking
 */
async function updateEmailInvitationStatus(token: string): Promise<void> {
  logger.info('Updating email invitation status', { token });

  try {
    const { error } = await supabase
      .from('contract_email_invitations')
      .update({
        status: 'submitted',
        submitted_at: new Date().toISOString(),
      })
      .eq('unique_token', token);

    if (error) {
      logger.error(
        'Failed to update email invitation',
        {
          error: getErrorMessage(error, 'Failed to update email invitation'),
          token,
        },
        'submission',
      );
      // Non-fatal error - don't throw
    } else {
      logger.info('Email invitation updated successfully', { token });
    }
  } catch (error) {
    logger.error(
      'Error updating email invitation',
      {
        error: getErrorMessage(error, 'Error updating email invitation'),
        token,
      },
      'submission',
    );
    // Non-fatal error
  }
}

/**
 * Trigger confirmation notifications
 * Phase 6: Placeholder for future edge function implementation
 * Future: Call edge function to send user confirmation email and notify company admin
 */
async function triggerConfirmationNotifications(
  submissionId: string,
): Promise<void> {
  logger.info('Triggering confirmation notifications', { submissionId });

  // Future implementation:
  // 1. Call edge function to send user confirmation email
  // 2. Call edge function to notify company admin
  // 3. Create notification records in messages table
  // 4. Log notification events to audit_logs

  // For now, just log the action
  logger.info('Confirmation notifications triggered (placeholder)', {
    submissionId,
    note: 'Email notifications will be implemented via edge functions',
  });
}

export async function createContractEmailInvitation(
  params: CreateContractEmailInvitationInput,
) {
  const {
    contractId,
    email,
    sentByUserId,
    emailContent,
    url,
    emailSubject,
    contractName,
    status = 'sent',
    sentAt,
    uniqueToken,
  } = params;

  let resolvedSentByUserId = sentByUserId;

  if (!resolvedSentByUserId) {
    const { data: userData, error: userError } = await supabase.auth.getUser();

    if (userError || !userData?.user?.id) {
      throw new Error('Authenticated user is required to create invitation');
    }

    resolvedSentByUserId = userData.user.id;
  }

  const { data, error } = await supabase
    .from('contract_email_invitations')
    .insert({
      contract_id: contractId,
      sent_by: resolvedSentByUserId,
      email_address: email,
      unique_token: uniqueToken || crypto.randomUUID(),
      status,
      email_subject:
        emailSubject ||
        `Information Request: Contract #${contractName || contractId}`,
      email_content: emailContent,
      personalized_url: url,
      sent_at: sentAt || (status === 'sent' ? new Date().toISOString() : null),
    })
    .select('*')
    .single();

  if (error) throw error;

  return data;
}

export interface CreateContractEmailInvitationInput {
  contractId: string;
  email: string;
  sentByUserId?: string;
  emailContent: string;
  url: string;
  emailSubject?: string;
  contractName?: string;
  status?: 'sent' | 'delivered' | 'opened' | 'clicked' | 'submitted' | 'failed';
  sentAt?: string;
  uniqueToken?: string;
}

/**
 * Result of finalizing signed contract artifacts.
 */
export interface SignedArtifactResult {
  /** ID of the user_documents row created for the signed PDF, or null if the
   *  vault session was not active / not authenticated. */
  userDocumentId: string | null;
  /** Whether the PDF was successfully delivered to company storage. */
  companyDeliveryStatus: 'success' | 'failed';
  /** Storage path in the company-signed-contracts bucket, when delivery succeeded. */
  companyDeliveryPath: string | null;
  /** Human-readable error message when delivery failed. */
  deliveryError?: string;
}

/**
 * Finalize signed contract artifacts after submission completion.
 *
 * Runs after `completeContractSubmission` (or `uploadSignedPDF` for physical)
 * and performs, in order:
 *  1. Store the signed PDF in the user's encrypted vault (user_documents), if
 *     the user has an active encryption session.
 *  2. Deliver the signed PDF to the company's private storage bucket.
 *  3. Update the contract_submissions row with the new tracking fields
 *     (signed_user_document_id, company_delivery_status, company_delivery_path,
 *     company_delivered_at, company_delivery_error) atomically.
 *
 * The submission's completion status is never rolled back; delivery failure is
 * surfaced through the returned result and stored for retry/monitoring.
 */
export async function finalizeSignedContractArtifacts(params: {
  submissionId: string;
  pdfBlob: Blob;
  contractName: string;
}): Promise<SignedArtifactResult> {
  const { submissionId, pdfBlob, contractName } = params;

  logger.info('Finalizing signed contract artifacts', { submissionId });

  // --- 1. Fetch submission context ----------------------------------------
  const { data: submission, error: fetchError } = await supabase
    .from('contract_submissions')
    .select(
      `
      id,
      user_id,
      signed_pdf_path,
      data_contracts!inner(
        company_id,
        name
      )
    `,
    )
    .eq('id', submissionId)
    .maybeSingle();

  if (fetchError) {
    logger.error('Failed to fetch submission context', {
      submissionId,
      error: getErrorMessage(fetchError, 'Fetch error'),
    });
  }

  const userId = submission?.user_id ?? null;
  const dataContractRaw = submission?.data_contracts as
    | { company_id?: string; name?: string }
    | Array<{ company_id?: string; name?: string }>
    | null
    | undefined;
  const dataContract = Array.isArray(dataContractRaw)
    ? (dataContractRaw[0] ?? null)
    : (dataContractRaw ?? null);
  const companyId: string =
    (dataContract as { company_id?: string } | null)?.company_id ?? 'unknown';

  // --- 2. Store in user vault (best-effort) --------------------------------
  let userDocumentId: string | null = null;

  if (userId) {
    try {
      const masterKey = getCachedMasterKey(userId);
      if (masterKey) {
        const sanitizedContractName = contractName.replace(/[^a-z0-9]/gi, '_');
        const fileName = `${sanitizedContractName}_signed.pdf`;
        const file = new File([pdfBlob], fileName, {
          type: 'application/pdf',
        });

        const result = await uploadDocument({
          file,
          documentType: 'signed_contract',
          title: `${contractName} – Signed Contract`,
          tags: ['contract', 'signed'],
        });

        if (result.success && result.document) {
          userDocumentId = result.document.id;
          logger.info('Signed PDF stored in user vault', {
            submissionId,
            documentId: userDocumentId,
          });
        } else {
          logger.warn('uploadDocument returned failure for signed contract', {
            submissionId,
            error: result.error,
          });
        }
      } else {
        logger.info(
          'No active vault session; skipping user-vault storage for signed contract',
          { submissionId },
        );
      }
    } catch (vaultErr) {
      logger.warn('Could not store signed PDF in user vault', {
        submissionId,
        error: getErrorMessage(vaultErr, 'Vault storage error'),
      });
    }
  }

  // --- 3. Deliver to company storage ---------------------------------------
  let companyDeliveryStatus: 'success' | 'failed' = 'failed';
  let companyDeliveryPath: string | null = null;
  let deliveryError: string | undefined;

  try {
    const provider = new SameProjectCompanyDeliveryProvider();
    const timestampedFileName = `${Date.now()}_signed.pdf`;
    const result = await provider.deliverSignedContract({
      submissionId,
      companyId,
      pdfBlob,
      fileName: timestampedFileName,
    });
    companyDeliveryPath = result.storagePath;
    companyDeliveryStatus = 'success';
  } catch (deliveryErr) {
    deliveryError = getErrorMessage(deliveryErr, 'Company delivery failed');
    logger.error('Failed to deliver signed contract to company storage', {
      submissionId,
      companyId,
      error: deliveryError,
    });
  }

  // --- 3.5 Ensure legacy PDF path fallback is always populated -------------
  let legacySignedPdfUrl: string | null =
    typeof submission?.signed_pdf_path === 'string'
      ? submission.signed_pdf_path
      : companyDeliveryStatus === 'success'
        ? companyDeliveryPath
        : null;

  if (!legacySignedPdfUrl) {
    try {
      const legacyStoragePath = `${submissionId}/${Date.now()}_signed_fallback.pdf`;

      const { error: legacyUploadError } = await supabase.storage
        .from('contract-submissions')
        .upload(legacyStoragePath, pdfBlob, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (legacyUploadError) {
        throw legacyUploadError;
      }

      const { data: legacyUrlData } = supabase.storage
        .from('contract-submissions')
        .getPublicUrl(legacyStoragePath);

      legacySignedPdfUrl = legacyUrlData.publicUrl;
    } catch (legacyPathError) {
      logger.warn('Could not create legacy signed_pdf_path fallback', {
        submissionId,
        error: getErrorMessage(legacyPathError, 'Legacy fallback error'),
      });
    }
  }

  // --- 4. Update submission tracking fields atomically ---------------------
  type SubmissionUpdate =
    Database['public']['Tables']['contract_submissions']['Update'];

  const updatePayload: SubmissionUpdate = {
    signed_pdf_path: legacySignedPdfUrl,
    generated_pdf_path: legacySignedPdfUrl,
    company_delivery_status: companyDeliveryStatus,
    company_delivery_path: companyDeliveryPath,
    company_delivery_error: deliveryError ?? null,
    ...(companyDeliveryStatus === 'success' && {
      company_delivered_at: new Date().toISOString(),
    }),
    ...(userDocumentId && { signed_user_document_id: userDocumentId }),
  };

  let { error: updateError } = await supabase
    .from('contract_submissions')
    .update(updatePayload)
    .eq('id', submissionId);

  const updateErrorRecord = updateError as
    | { code?: string; message?: string }
    | undefined;
  const hasMissingColumnError =
    updateErrorRecord?.code === 'PGRST204' ||
    updateErrorRecord?.message?.includes('Could not find the') ||
    updateErrorRecord?.message?.includes('does not exist') ||
    false;

  if (updateError && hasMissingColumnError) {
    logger.warn(
      'Submission tracking columns missing; retrying with legacy fields only',
      {
        submissionId,
        error: getErrorMessage(updateError, 'Missing column error'),
      },
    );

    const legacyOnlyPayload: SubmissionUpdate = {
      signed_pdf_path: legacySignedPdfUrl,
      generated_pdf_path: legacySignedPdfUrl,
    };

    const retryResult = await supabase
      .from('contract_submissions')
      .update(legacyOnlyPayload)
      .eq('id', submissionId);

    updateError = retryResult.error;
  }

  if (updateError) {
    logger.error('Failed to update submission with artifact tracking fields', {
      submissionId,
      error: getErrorMessage(updateError, 'Update error'),
    });
  }

  logger.info('Signed contract artifact finalization complete', {
    submissionId,
    userDocumentId,
    companyDeliveryStatus,
    companyDeliveryPath,
  });

  return {
    userDocumentId,
    companyDeliveryStatus,
    companyDeliveryPath,
    deliveryError,
  };
}
